/**
 * Animated Hero Slideshow Component
 * Crossfade-based slideshow with autoplay and keyboard navigation.
 */

const prefersReducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

/**
 * Animated Hero Slideshow custom element
 */
class AnimatedHeroSlideshow extends HTMLElement {
  constructor() {
    super();
    
    this._current = 0;
    this._interval = undefined;
    
    // Touch/swipe tracking
    this._touchStartX = 0;
    this._touchStartY = 0;
    this._touchEndX = 0;
    this._touchEndY = 0;
    this._isSwiping = false;
    
    // Bind methods
    this.handleVisibilityChange = this.handleVisibilityChange.bind(this);
    this.suspend = this.suspend.bind(this);
    this.resume = this.resume.bind(this);
    this.handleTouchStart = this.handleTouchStart.bind(this);
    this.handleTouchMove = this.handleTouchMove.bind(this);
    this.handleTouchEnd = this.handleTouchEnd.bind(this);
  }

  connectedCallback() {
    this.refs = this.getRefs();
    
    const slideCount = this.slides?.length || 0;
    
    if (slideCount <= 1) {
      this.setupSingleSlide();
    } else {
      this.setupSlideshow();
    }
  }

  disconnectedCallback() {
    if (this.slides?.length > 1) {
      this.removeEventListener('mouseenter', this.suspend);
      this.removeEventListener('mouseleave', this.resume);
      document.removeEventListener('visibilitychange', this.handleVisibilityChange);
      
      // Remove touch event listeners
      this.removeEventListener('touchstart', this.handleTouchStart);
      this.removeEventListener('touchmove', this.handleTouchMove);
      this.removeEventListener('touchend', this.handleTouchEnd);
    }

    this.suspend();
  }

  getRefs() {
    return {
      scroller: this.querySelector('[ref="scroller"]') || this.querySelector('.animated-hero-slideshow__slides'),
      slideshowControls: this.querySelector('[ref="slideshowControls"]') || this.querySelector('.animated-hero-controls'),
      slides: Array.from(this.querySelectorAll('[ref="slides[]"]') || this.querySelectorAll('.animated-hero-slide')),
      dots: Array.from(this.querySelectorAll('[ref="dots[]"]')),
      current: this.querySelector('[ref="current"]'),
      previous: this.querySelector('[ref="previous"]'),
      next: this.querySelector('[ref="next"]')
    };
  }

  get slides() {
    return this.refs?.slides?.filter(slide => !slide.hasAttribute('hidden'));
  }

  get current() {
    return this._current;
  }

  set current(value) {
    const { current, dots, slides, previous, next } = this.refs;

    this._current = value;

    if (current) current.textContent = `${value + 1}`;

    dots?.forEach((el, i) => el.setAttribute('aria-selected', `${i === value}`));

    if (previous) previous.disabled = Boolean(!this.infinite && value === 0);
    if (next) next.disabled = Boolean(!this.infinite && slides && value >= slides.length - 1);
    
    // Update aria-hidden on all slides for crossfade
    this.slides?.forEach((slide, i) => {
      slide.setAttribute('aria-hidden', i !== value ? 'true' : 'false');
    });
  }

  get infinite() {
    return this.getAttribute('infinite') != null;
  }

  get autoplay() {
    return Boolean(this.autoplayInterval);
  }

  get autoplayInterval() {
    const interval = this.getAttribute('autoplay');
    const value = parseInt(`${interval}`, 10);
    return Number.isNaN(value) ? undefined : value * 1000;
  }

  get paused() {
    return this.hasAttribute('paused');
  }

  set paused(value) {
    if (value) {
      this.setAttribute('paused', '');
    } else {
      this.removeAttribute('paused');
    }
  }

  get previousIndex() {
    return this.current - 1;
  }

  get nextIndex() {
    return this.current + 1;
  }

  get initialSlideIndex() {
    const initialSlide = this.getAttribute('initial-slide');
    return initialSlide != null ? parseInt(initialSlide, 10) : 0;
  }

  setupSingleSlide() {
    this.current = 0;
    if (this.hasAttribute('auto-hide-controls') && this.refs.slideshowControls) {
      this.refs.slideshowControls.hidden = true;
    }
    if (this.refs.slides?.[0]) {
      this.refs.slides[0].setAttribute('aria-hidden', 'false');
    }
  }

  setupSlideshow() {
    this.addEventListener('mouseenter', this.suspend);
    this.addEventListener('mouseleave', this.resume);
    document.addEventListener('visibilitychange', this.handleVisibilityChange);

    // Setup click handlers for controls
    this.setupControlHandlers();
    
    // Setup touch/swipe handlers for mobile
    this.setupTouchHandlers();

    // Set initial slide
    this.current = this.initialSlideIndex;

    this.resume();
  }

  setupControlHandlers() {
    // Arrow buttons
    this.querySelectorAll('[data-action="previous"]').forEach(btn => {
      btn.addEventListener('click', (e) => this.previous(e));
    });
    
    this.querySelectorAll('[data-action="next"]').forEach(btn => {
      btn.addEventListener('click', (e) => this.next(e));
    });
    
    // Dot buttons
    this.querySelectorAll('[data-action="select"]').forEach(btn => {
      btn.addEventListener('click', (e) => {
        const index = parseInt(btn.dataset.index, 10);
        this.select(index, e);
      });
    });
    
    // Play/pause buttons
    this.querySelector('[data-action="pause"]')?.addEventListener('click', () => this.pause());
    this.querySelector('[data-action="play"]')?.addEventListener('click', () => this.play());
  }

  setupTouchHandlers() {
    // Add touch event listeners for swipe navigation on touch devices
    this.addEventListener('touchstart', this.handleTouchStart, { passive: true });
    this.addEventListener('touchmove', this.handleTouchMove, { passive: false });
    this.addEventListener('touchend', this.handleTouchEnd, { passive: true });
  }

  handleTouchStart(event) {
    // Get the first touch point
    const touch = event.touches[0];
    this._touchStartX = touch.clientX;
    this._touchStartY = touch.clientY;
    this._touchEndX = touch.clientX;
    this._touchEndY = touch.clientY;
    this._isSwiping = false;
  }

  handleTouchMove(event) {
    if (!event.touches.length) return;
    
    const touch = event.touches[0];
    this._touchEndX = touch.clientX;
    this._touchEndY = touch.clientY;
    
    // Calculate the difference
    const deltaX = Math.abs(this._touchEndX - this._touchStartX);
    const deltaY = Math.abs(this._touchEndY - this._touchStartY);
    
    // If horizontal movement is greater than vertical, we're swiping horizontally
    // Prevent default to stop vertical scroll and enable smooth horizontal swipe
    if (deltaX > deltaY && deltaX > 10) {
      this._isSwiping = true;
      event.preventDefault();
    }
  }

  handleTouchEnd() {
    // Minimum swipe distance threshold (in pixels)
    const minSwipeDistance = 50;
    
    const deltaX = this._touchEndX - this._touchStartX;
    const deltaY = Math.abs(this._touchEndY - this._touchStartY);
    const absX = Math.abs(deltaX);
    
    // Only trigger if:
    // 1. Horizontal distance exceeds threshold
    // 2. Horizontal movement is greater than vertical (prevents accidental swipes while scrolling)
    if (absX >= minSwipeDistance && absX > deltaY) {
      if (deltaX < 0) {
        // Swiped left -> go to next slide
        this.next();
      } else {
        // Swiped right -> go to previous slide
        this.previous();
      }
    }
    
    // Reset tracking values
    this._touchStartX = 0;
    this._touchStartY = 0;
    this._touchEndX = 0;
    this._touchEndY = 0;
    this._isSwiping = false;
  }

  select(input, event) {
    const { slides } = this;
    if (!slides?.length) return;

    let requestedIndex = typeof input === 'number' ? input : parseInt(input, 10);

    if (requestedIndex === undefined || isNaN(requestedIndex)) return;

    event?.preventDefault();

    const lastIndex = slides.length - 1;

    let index = requestedIndex;
    
    // Handle infinite looping
    if (this.infinite) {
      if (requestedIndex < 0) index = lastIndex;
      else if (requestedIndex > lastIndex) index = 0;
    } else {
      index = clamp(requestedIndex, 0, lastIndex);
    }

    this.current = index;
  }

  next(event) {
    event?.preventDefault();
    this.select(this.nextIndex, event);
  }

  previous(event) {
    event?.preventDefault();
    this.select(this.previousIndex, event);
  }

  play(interval = this.autoplayInterval) {
    if (this._interval) return;

    this.paused = false;

    this._interval = setInterval(() => {
      if (this.matches(':hover') || document.hidden) return;
      this.next();
    }, interval);
  }

  pause() {
    this.paused = true;
    this.suspend();
  }

  suspend() {
    clearInterval(this._interval);
    this._interval = undefined;
  }

  resume() {
    if (!this.autoplay || this.paused) return;
    this.suspend();
    this.play();
  }

  handleVisibilityChange() {
    document.hidden ? this.suspend() : this.resume();
  }
}

// Register custom element
if (!customElements.get('animated-hero-slideshow')) {
  customElements.define('animated-hero-slideshow', AnimatedHeroSlideshow);
}

// Shopify theme editor support
if (window.Shopify && window.Shopify.designMode) {
  document.addEventListener('shopify:section:load', (event) => {
    const section = event.target.querySelector('animated-hero-slideshow');
    if (section) {
      // Force re-initialization
      const parent = section.parentNode;
      const clone = section.cloneNode(true);
      parent.replaceChild(clone, section);
    }
  });
}
